import { useEffect, useState, type CSSProperties } from 'react';
import { LIMITS } from '@shared/constants';
import type { Routine } from '@shared/schemas';
import { useT } from '../i18n';
import type { DisplaySymbol } from '../lib/board';
import { currentStep, doneSteps, restart, toggleStep, type RoutineProgress } from '../lib/routine';
import * as storage from '../lib/storage';
import { Pictogram } from './Pictogram';

export interface StepSymbol {
  symbol: DisplaySymbol;
  color: string;
}

interface Props {
  userId: string;
  routines: Routine[];
  /** The board symbol behind a step (null when it was deleted). */
  find(id: string): StepSymbol | null;
  canEdit: boolean;
  onSpeak(text: string): void;
  onNew(): void;
  onEdit(routine: Routine): void;
  onDelete(routine: Routine): void;
  onMove(id: string, delta: -1 | 1): void;
}

/**
 * "Rotina": the day's sequence (or "Primeiro → Depois") with big pictures. The
 * child taps a step to hear it and mark it done; the next one is highlighted.
 */
export function RoutineView({ userId, routines, find, canEdit, onSpeak, onNew, onEdit, onDelete, onMove }: Props) {
  const t = useT();
  const selectedKey = `routine-selected:${userId}`;
  const progressKey = `routine-progress:${userId}`;
  const [selectedId, setSelectedId] = useState<string | null>(() => storage.readJson<string>(selectedKey));
  const [progress, setProgress] = useState<RoutineProgress>(() => storage.readJson<RoutineProgress>(progressKey) ?? {});

  const routine = routines.find((r) => r.id === selectedId) ?? routines[0] ?? null;

  useEffect(() => {
    storage.writeJson(progressKey, progress);
  }, [progress, progressKey]);

  function select(id: string) {
    setSelectedId(id);
    storage.writeJson(selectedKey, id);
  }

  if (!routine) {
    return (
      <section className="category routine" aria-labelledby="routine-title">
        <h1 id="routine-title" className="sr-only">
          {t('routine')}
        </h1>
        <div className="routine__intro">
          <p>{t('routineIntro')}</p>
          {canEdit && (
            <button type="button" className="btn btn--primary" onClick={onNew}>
              ➕ {t('routineNew')}
            </button>
          )}
        </div>
      </section>
    );
  }

  // Steps whose symbol was deleted are skipped (the indexes still refer to routine.steps).
  const steps = routine.steps.map((id, index) => ({ index, step: find(id) }));
  const shown = steps.filter((s): s is { index: number; step: StepSymbol } => !!s.step);
  const done = doneSteps(progress, routine).filter((i) => shown.some((s) => s.index === i));
  const now = currentStep(routine.steps.length, [...done, ...steps.filter((s) => !s.step).map((s) => s.index)]);
  const finished = shown.length > 0 && now === -1;

  function press(index: number, label: string) {
    const wasDone = done.includes(index);
    if (!wasDone) {
      const prefix = routine!.kind === 'firstThen' ? `${index === 0 ? t('first') : t('then')}, ` : '';
      onSpeak(prefix + label);
    }
    setProgress((p) => toggleStep(p, routine!, index));
  }

  const card = ({ index, step }: { index: number; step: StepSymbol }, heading?: string) => {
    const isDone = done.includes(index);
    const isNow = index === now;
    return (
      <li key={index} className="routine__item">
        {heading && <p className="routine__heading">{heading}</p>}
        <button
          type="button"
          className={`step${isDone ? ' step--done' : ''}${isNow ? ' step--now' : ''}`}
          style={{ '--accent': step.color } as CSSProperties}
          onClick={() => press(index, step.symbol.label)}
          aria-pressed={isDone}
          aria-label={isDone ? t('routineStepDone', { word: step.symbol.label }) : step.symbol.label}
        >
          {routine.kind === 'sequence' && (
            <span className="step__number" aria-hidden="true">
              {index + 1}
            </span>
          )}
          {isNow && (
            <span className="step__now" aria-hidden="true">
              {t('routineNow')}
            </span>
          )}
          <Pictogram source={step.symbol.picto} className="step__picto" />
          <span className="step__label">{step.symbol.label}</span>
          {isDone && (
            <span className="step__check" aria-hidden="true">
              ✓
            </span>
          )}
        </button>
      </li>
    );
  };

  return (
    <section className="category routine" aria-labelledby="routine-title">
      <h1 id="routine-title" className="sr-only">
        {routine.name}
      </h1>

      {(routines.length > 1 || canEdit) && (
        <div className="routine__tabs" role="group" aria-label={t('routineChoose')}>
          {routines.map((r) => (
            <button key={r.id} type="button" className="chip chip--link" aria-pressed={r.id === routine.id} onClick={() => select(r.id)}>
              {r.kind === 'firstThen' ? '➡️' : '🗓️'} {r.name}
            </button>
          ))}
          {canEdit && routines.length < LIMITS.maxRoutines && (
            <button type="button" className="chip chip--link chip--new" onClick={onNew}>
              ➕ {t('routineNew')}
            </button>
          )}
        </div>
      )}

      {canEdit && (
        <div className="category__tools">
          <button type="button" className="icon-btn" onClick={() => onMove(routine.id, -1)} aria-label={t('moveRoutineLeft')}>
            ◀
          </button>
          <button type="button" className="icon-btn" onClick={() => onMove(routine.id, 1)} aria-label={t('moveRoutineRight')}>
            ▶
          </button>
          <button type="button" className="btn btn--small btn--ghost" onClick={() => onEdit(routine)}>
            ✏️ {t('routineEdit')}
          </button>
          <button type="button" className="btn btn--small btn--danger-ghost" onClick={() => onDelete(routine)}>
            🗑️ {t('routineDelete')}
          </button>
        </div>
      )}

      {shown.length === 0 ? (
        <p className="muted routine__intro">{t('routineMissingSteps')}</p>
      ) : routine.kind === 'firstThen' ? (
        <ol className="routine__first-then">
          {shown[0] && card(shown[0], t('first'))}
          {shown[1] && (
            <>
              <li className="routine__arrow" aria-hidden="true">
                ➜
              </li>
              {card(shown[1], t('then'))}
            </>
          )}
        </ol>
      ) : (
        <ol className="routine__steps">{shown.map((s) => card(s))}</ol>
      )}

      {finished && (
        <p className="routine__finished" role="status">
          🎉 {t('routineDone')}
        </p>
      )}
      {done.length > 0 && (
        <div className="routine__footer">
          <button type="button" className="btn btn--secondary" onClick={() => setProgress((p) => restart(p, routine))}>
            ↺ {t('routineRestart')}
          </button>
        </div>
      )}
    </section>
  );
}
